import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── UNIFIED APP CONTEXT ───────────────────────────────────────────────────────
const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('desky_token'));
  const [tenant, setTenant] = useState(() => {
    try { return JSON.parse(localStorage.getItem('desky_tenant')); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);
  const [themeFamily, setThemeFamily] = useState(localStorage.getItem('desky_theme') || 'school');
  // `logout` is defined below; the storage listener needs it without taking a
  // dependency on declaration order.
  const logoutRef = useRef(() => {});

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.get(`${API}/auth/me`)
        .then(res => setUser(res.data))
        .catch(() => { setToken(null); localStorage.removeItem('desky_token'); localStorage.removeItem('desky_user'); localStorage.removeItem('desky_tenant'); })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    document.getElementById('app-root')?.setAttribute('data-theme-family', themeFamily);
  }, [themeFamily]);

  // ── Cross-tab session guard ──────────────────────────────────────────────
  // The session lives in localStorage, which every tab on this origin shares,
  // so logging in as someone else in ANY tab silently replaces the token here
  // too. This tab would keep rendering the old user while sending the new
  // user's JWT — every action then gets recorded against the wrong person
  // (e.g. a DeskeyHUB document submitted by Teacher1 stored as the admin who
  // logged in next door). `storage` only fires in the OTHER tabs, which is
  // exactly the stale ones. Re-sync so the UI can never lie about who is
  // acting.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== 'desky_token') return; // null = storage cleared
      const next = localStorage.getItem('desky_token');
      if (next === token) return;
      if (!next) { logoutRef.current(); return; }
      // A different identity owns the session now — reload so this tab
      // re-authenticates as whoever that actually is.
      window.location.reload();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [token]);

  const login = useCallback(async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData, tenant: tenantData } = res.data;
    localStorage.setItem('desky_token', access_token);
    localStorage.setItem('desky_user', JSON.stringify(userData));
    if (tenantData) {
      localStorage.setItem('desky_tenant', JSON.stringify(tenantData));
      setTenant(tenantData);
    }
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('desky_token');
    localStorage.removeItem('desky_user');
    localStorage.removeItem('desky_tenant');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setTenant(null);
  }, []);
  logoutRef.current = logout;

  const applyTheme = useCallback((family) => {
    document.getElementById('app-root')?.setAttribute('data-theme-family', family);
    localStorage.setItem('desky_theme', family);
    setThemeFamily(family);
  }, []);

  return (
    <AppContext.Provider value={{ user, token, tenant, loading, login, logout, themeFamily, applyTheme }}>
      {children}
    </AppContext.Provider>
  );
};

// ─── API HELPERS ─────────────────────────────────────────────────────────────
export const apiCall = async (method, path, data = null, params = null) => {
  const config = { method, url: `${API}${path}`, params };
  if (data) config.data = data;
  const res = await axios(config);
  return res.data;
};

export { API, BACKEND_URL };
